import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';
import { from, Observable, take } from 'rxjs';

import { KlConfirmDialogService } from '@core/services/kl-confirm-dialog.service';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly keycloak = inject(Keycloak);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  private loggingOut = false;
  private refreshInFlight: Promise<boolean> | null = null;

  /**
   * Forces a Keycloak token refresh (`updateToken(-1)`).
   * Concurrent callers share one in-flight refresh.
   */
  refreshSession(): Observable<boolean> {
    if (this.refreshInFlight == null) {
      this.refreshInFlight = this.keycloak
        .updateToken(-1)
        .then(() => true)
        .catch(() => false)
        .finally(() => {
          this.refreshInFlight = null;
        });
    }
    return from(this.refreshInFlight);
  }

  /** Legacy-parity forced sign-out when the session is no longer valid (e.g. HTTP 401). */
  logoutUnauthorized(): void {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;

    this.injector
      .get(KlConfirmDialogService)
      .open({
        title: 'session_expired',
        description: 'session_expired_message',
        confirmLabel: 'understood',
        icon: 'ph ph-clock-countdown',
        tone: 'warning',
        hideCancel: true,
        blurMask: true,
        closable: false,
        dismissableMask: false,
        closeOnEscape: false,
      })
      .pipe(take(1))
      .subscribe((ok) => {
        if (!ok) {
          this.loggingOut = false;
          return;
        }
        this.performLogout();
      });
  }

  private performLogout(): void {
    void this.keycloak.logout({ redirectUri: window.location.origin }).then(() => {
      void this.router.navigateByUrl('/auth');
    });
  }
}
